<script setup lang="ts">
import { StarIcon } from "@heroicons/vue/24/solid";
import { computed } from "vue";
import { displayedCategory } from "../composables/useHotelCategory";
import type { HotelCategory } from "../interfaces/hotelCategory";

/**
 * Rating del hotel en la card de disponibilidad: sólo las estrellas en una pastilla,
 * con el mismo lenguaje que las demás etiquetas de la card (cancelación, tipo de
 * alojamiento). El número va en el popover al pasar el cursor, para no cargar la card.
 *
 * El rating es el del catálogo (el del hotel o, si no tiene, el de su marca). Sin
 * rating la pastilla va en gris para que no se lea como una mala calificación.
 *
 * En Hotelbeds manda la categoría oficial del proveedor: estrellas cuando son
 * estrellas, y su nombre tal cual cuando no («3 LLAVES», «HOSTAL 2*»).
 */
interface Props {
  rating: number | null;
  provider?: string;
  category?: HotelCategory | null;
}

const props = defineProps<Props>();

const display = computed(() => displayedCategory(props));

const stars = computed(() => display.value.stars);

const label = computed(() => display.value.label);
</script>

<template>
  <span
    v-if="display.text"
    v-tooltip="label"
    :aria-label="label"
    class="inline-flex w-fit cursor-default items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-100"
  >
    {{ display.text }}
  </span>
  <span
    v-else
    v-tooltip="label"
    role="img"
    :aria-label="label"
    :class="[
      'inline-flex w-fit cursor-default items-center rounded-full px-1.5 py-1 ring-1',
      stars > 0 ? 'bg-blue-50 ring-blue-100' : 'bg-gray-50 ring-gray-200',
    ]"
  >
    <StarIcon
      v-for="star in 5"
      :key="star"
      :class="[
        'h-3.5 w-3.5',
        stars === 0
          ? 'text-gray-300'
          : star <= stars
            ? 'text-blue-600'
            : 'text-blue-200',
      ]"
      aria-hidden="true"
    />
  </span>
</template>
